import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
    Trash2, Loader2,
    AlertCircle, Plus,
    Search, Mail, Check,
    ArrowUpDown, ArrowUp, ArrowDown,
} from 'lucide-react';
import { V2Button } from '../../../shared/custom-ui/v2-components';
import TeamSkeleton from '../../../shared/custom-ui/skeleton/TeamSkeleton/TeamSkeleton';

const AVATAR_PALETTE = [
    '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b',
    '#10b981', '#06b6d4', '#f97316', '#6366f1', '#14b8a6',
];

const getAvatarColor = (str: string): string => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
    return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
};

const getInitials = (name: string): string => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

interface TeamLayoutProps {
    data: any[];
    loading?: boolean;
    error?: string;
    onDelete?: (row: any) => void;
    scale?: number;
    roles?: string[];
    onAssignRole?: (email: string, role: string) => Promise<void>;
    onRemoveRole?: (email: string) => Promise<void>;
    onAddMember?: () => void;
    onRetry?: () => void;
    /** Search query controlled by TeamManagement */
    search?: string;
}

type SortDir = 'asc' | 'desc' | null;

const COLS = 'grid-cols-[44px_minmax(0,1.8fr)_minmax(0,1.2fr)_minmax(0,0.8fr)_minmax(0,1.3fr)]';

const RoleSelector: React.FC<{
    email: string;
    currentRole?: string;
    roles: string[];
    onAssign: (email: string, role: string) => Promise<void>;
    onRemove: (email: string) => Promise<void>;
    compact?: boolean;
}> = ({ email, currentRole, roles, onAssign, onRemove, compact = false }) => {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
    const triggerRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    const openMenu = () => {
        if (!triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        const menuH = Math.min(roles.length * 40 + 100, 320);
        const spaceBelow = window.innerHeight - rect.bottom;
        const above = spaceBelow < menuH && rect.top > menuH;
        setMenuStyle({
            position: 'fixed',
            left: Math.min(rect.left, window.innerWidth - 248),
            width: Math.max(rect.width, 230),
            zIndex: 9999,
            ...(above ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
        });
        setOpen(true);
    };

    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            if (triggerRef.current?.contains(e.target as Node)) return;
            if (menuRef.current?.contains(e.target as Node)) return;
            setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const handleAssign = async (role: string) => {
        setOpen(false);
        if (busy) return;
        setBusy(true);
        try { await onAssign(email, role); }
        finally { setBusy(false); }
    };

    const handleRemove = async () => {
        setOpen(false);
        if (busy) return;
        setBusy(true);
        try { await onRemove(email); }
        finally { setBusy(false); }
    };

    const hasRole = !!currentRole;
    const chipColor = getAvatarColor(currentRole || email);

    if (busy) {
        return (
            <div className="flex items-center gap-1.5 h-8 px-2 text-[12px]" style={{ color: 'var(--v2-text-muted)' }}>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span>Saving…</span>
            </div>
        );
    }

    return (
        <div className="relative inline-flex">
            {/* ── Trigger chip ── */}
            <button
                ref={triggerRef}
                type="button"
                onClick={openMenu}
                title={hasRole ? `Role: ${currentRole} — click to change` : 'Click to assign a role'}
                className="group inline-flex items-center gap-2 rounded-lg text-[12.5px] font-semibold transition-all duration-150 hover:opacity-90"
                style={{
                    outline: 'none',
                    height: compact ? '30px' : '36px',
                    maxWidth: '180px',
                    minWidth: hasRole ? '80px' : '120px',
                    paddingLeft: '10px',
                    paddingRight: '8px',
                    border: hasRole ? `1.5px solid ${chipColor}40` : '1.5px dashed var(--v2-border-interactive)',
                    background: hasRole ? `${chipColor}12` : 'transparent',
                    color: hasRole ? chipColor : 'var(--v2-text-muted)',
                }}
            >
                {hasRole ? (
                    <>
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: chipColor }} />
                        <span className="truncate flex-1 text-left">{currentRole}</span>
                        {/* Subtle edit pencil — signals the chip is editable */}
                        <svg
                            className="w-3 h-3 flex-shrink-0 opacity-0 group-hover:opacity-50 transition-opacity duration-150"
                            fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round"
                                d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" />
                        </svg>
                    </>
                ) : (
                    <>
                        <Plus className="w-3 h-3 flex-shrink-0" />
                        <span className="flex-1 text-left">Assign Role</span>
                    </>
                )}
            </button>

            {/* ── Dropdown menu (portal) ── */}
            {open && ReactDOM.createPortal(
                <div
                    ref={menuRef}
                    style={{
                        ...menuStyle,
                        background: 'var(--bg-elevated, var(--bg-card))',
                        border: '1px solid var(--border-subtle)',
                        borderRadius: '12px',
                        padding: '6px',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.18), 0 4px 16px rgba(0,0,0,0.10)',
                        maxHeight: '320px',
                        overflowY: 'auto',
                    }}
                    role="listbox"
                >
                    {/* Header */}
                    <p className="text-[10px] font-black uppercase tracking-wider px-3 py-1.5 mb-0.5" style={{ color: 'var(--text-muted)' }}>
                        {hasRole ? 'Change Role' : 'Assign Role'}
                    </p>

                    {/* Role options — only real roles, no confusing "No role assigned" */}
                    {roles.map(role => {
                        const isSelected = role === currentRole;
                        return (
                            <button
                                key={role}
                                type="button"
                                role="option"
                                aria-selected={isSelected}
                                onClick={() => handleAssign(role)}
                                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-[8px] text-[12.5px] text-left transition-colors duration-100 mb-0.5"
                                style={{
                                    outline: 'none',
                                    color: isSelected ? 'var(--v2-primary-600, #2563eb)' : 'var(--text-primary)',
                                    fontWeight: isSelected ? 650 : 450,
                                    background: isSelected ? 'color-mix(in srgb, var(--primary-500) 10%, transparent)' : 'transparent',
                                }}
                                onMouseEnter={e => { if (!isSelected) (e.currentTarget as HTMLElement).style.background = 'var(--bg-hover)'; }}
                                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = isSelected ? 'color-mix(in srgb, var(--primary-500) 10%, transparent)' : 'transparent'; }}
                            >
                                <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center">
                                    {isSelected && <Check className="w-3.5 h-3.5" style={{ color: 'var(--primary-500)', strokeWidth: 2.5 }} />}
                                </span>
                                <span className="flex-1 truncate">{role}</span>
                            </button>
                        );
                    })}

                    {/* Remove Role — only shown when a role is set, separated by a divider */}
                    {hasRole && (
                        <>
                            <div className="my-1.5 mx-2" style={{ height: '1px', background: 'var(--v2-border-subtle, rgba(0,0,0,0.08))' }} />
                            <button
                                type="button"
                                onClick={handleRemove}
                                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-[8px] text-[12.5px] text-left transition-colors duration-100"
                                style={{ outline: 'none', color: '#ef4444', fontWeight: 500 }}
                                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(239,68,68,0.08)'; }}
                                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                            >
                                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                    <circle cx="12" cy="12" r="9" />
                                    <path strokeLinecap="round" d="M9 9l6 6M15 9l-6 6" />
                                </svg>
                                <span>Remove Role</span>
                            </button>
                        </>
                    )}
                </div>,
                document.body
            )}
        </div>
    );
};

const NoResultsState: React.FC<{ query: string }> = ({ query }) => (
    <div className="flex flex-col items-center justify-center py-14 text-center">
        <Search className="w-10 h-10 mb-3" style={{ color: 'var(--v2-text-muted)', opacity: 0.35 }} />
        <p className="text-[15px] font-bold mb-1" style={{ color: 'var(--v2-text-heading)' }}>No results for "{query}"</p>
        <p className="text-[13px]" style={{ color: 'var(--v2-text-muted)' }}>Try a different name, email, or role.</p>
    </div>
);

const MembersView: React.FC<{
    data: any[];
    roles: string[];
    onDelete?: (row: any) => void;
    onAssignRole?: (email: string, role: string) => Promise<void>;
    onRemoveRole?: (email: string) => Promise<void>;
    sortDir: SortDir;
    onSort: () => void;
}> = ({ data, roles, onDelete, onAssignRole, onRemoveRole, sortDir, onSort }) => {
    const SortIcon = sortDir === 'asc' ? ArrowUp : sortDir === 'desc' ? ArrowDown : ArrowUpDown;

    return (
        <div className="flex flex-col gap-3 pb-4">

            {/* ── Desktop: column header row ── */}
            <div className="hidden lg:block">
                <div className={`depth-table-header grid ${COLS} gap-3 px-6 py-3.5 text-[11px] font-black uppercase tracking-wider whitespace-nowrap`}
                    style={{ color: 'var(--v2-text-muted)' }}>
                    <div>&nbsp;</div>
                    <div>
                        <button
                            type="button"
                            onClick={onSort}
                            className="inline-flex items-center gap-1.5 hover:opacity-80 transition-opacity"
                            style={{ outline: 'none', color: 'inherit' }}
                        >
                            Member / ID <SortIcon className="w-3 h-3" />
                        </button>
                    </div>
                    <div>Email</div>
                    <div>Role</div>
                    <div className="text-center">Actions</div>
                </div>
            </div>

            {/* ── Desktop: data rows ── */}
            <div className="hidden lg:flex lg:flex-col lg:gap-3">
                {data.map((row, idx) => {
                    const color = getAvatarColor(row.name || row.email || '');
                    const initials = getInitials(row.name || '');
                    const roleColor = row.role ? getAvatarColor(row.role) : null;
                    const shortId = (row.rad_id || '').length > 12
                        ? (row.rad_id || '').slice(0, 8).toUpperCase()
                        : (row.rad_id || 'N/A');

                    return (
                        <div
                            key={row.rad_id || row.email || idx}
                            className="group relative rounded-xl cursor-default overflow-hidden transition-all duration-200 border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] shadow-[var(--v2-shadow-card)] hover:shadow-[var(--v2-shadow-card-hover)] hover:border-[var(--v2-border-glass-strong)]"
                        >
                            <div className={`p-4 grid ${COLS} gap-3 items-center`}>

                                {/* Avatar — rounded-full with color ring, same as PatientAvatar */}
                                <div>
                                    <div
                                        className="w-10 h-10 rounded-full flex items-center justify-center text-white text-[13px] font-black"
                                        style={{
                                            background: `linear-gradient(135deg, ${color}, ${color}bb)`,
                                            boxShadow: `0 0 0 2px var(--v2-bg-card), 0 0 0 3.5px ${color}40`,
                                        }}
                                    >
                                        {initials}
                                    </div>
                                </div>

                                {/* Member name + short ID (like Patient / MRN) */}
                                <div className="overflow-hidden">
                                    <h4 className="font-semibold text-[15px] truncate whitespace-nowrap text-[var(--v2-text-primary)]">
                                        {row.name || 'Unknown Member'}
                                    </h4>
                                    <span
                                        className="text-[13px] font-mono whitespace-nowrap text-[var(--v2-text-muted)]"
                                        style={{ letterSpacing: '0.03em' }}
                                    >
                                        ID: {shortId}
                                    </span>
                                </div>

                                {/* Email with icon */}
                                <div className="overflow-hidden min-w-0">
                                    <span className="text-[14px] flex items-center gap-1.5 text-[var(--v2-text-primary)] min-w-0">
                                        <Mail className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--v2-text-muted)' }} />
                                        <span className="truncate">{row.email || '—'}</span>
                                    </span>
                                </div>

                                {/* Role column — RoleSelector IS the display + editor (no static badge duplicate) */}
                                <div onClick={e => e.stopPropagation()}>
                                    {roles.length > 0 && onAssignRole && onRemoveRole ? (
                                        <RoleSelector
                                            email={row.email}
                                            currentRole={row.role}
                                            roles={roles}
                                            onAssign={onAssignRole}
                                            onRemove={onRemoveRole}
                                            compact
                                        />
                                    ) : (
                                        /* Fallback read-only badge when no roles configured */
                                        row.role ? (
                                            <span
                                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[13px] font-medium border"
                                                style={{
                                                    background: `${roleColor}12`,
                                                    color: roleColor!,
                                                    borderColor: `${roleColor}30`,
                                                }}
                                            >
                                                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: roleColor! }} />
                                                {row.role}
                                            </span>
                                        ) : (
                                            <span
                                                className="text-[13px] italic"
                                                style={{ color: 'var(--v2-text-muted)' }}
                                            >
                                                —
                                            </span>
                                        )
                                    )}
                                </div>

                                {/* Actions — ONLY trash button (role editing is in Role column above) */}
                                <div className="flex items-center justify-center" onClick={e => e.stopPropagation()}>
                                    {onDelete && (
                                        <V2Button
                                            size="small"
                                            variant="ghost"
                                            leftIcon={<Trash2 style={{ width: 13, height: 13, color: 'var(--v2-danger-500, #ef4444)' }} />}
                                            onClick={() => onDelete(row)}
                                            title="Remove member"
                                        />
                                    )}
                                </div>
                            </div>

                            {/* Left accent bar — exact same as PatientsLayout */}
                            <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: color }} />
                        </div>
                    );
                })}
            </div>

            {/* ── Mobile card layout — mirrors PatientsLayout mobile ── */}
            <div className="lg:hidden flex flex-col gap-3 px-0.5">
                {data.map((row, idx) => {
                    const color = getAvatarColor(row.name || row.email || '');
                    const initials = getInitials(row.name || '');
                    const roleColor = row.role ? getAvatarColor(row.role) : null;
                    const shortId = (row.rad_id || '').length > 12
                        ? (row.rad_id || '').slice(0, 8).toUpperCase()
                        : (row.rad_id || 'N/A');

                    return (
                        <div
                            key={row.rad_id || row.email || idx}
                            className="relative rounded-xl overflow-hidden transition-all duration-200 active:scale-[0.99]"
                            style={{
                                background: 'var(--v2-bg-card)',
                                border: '1px solid var(--v2-border-card)',
                                borderLeft: `3px solid ${color}`,
                                boxShadow: '0 2px 8px -2px rgba(0,0,0,0.1), 0 1px 3px rgba(0,0,0,0.05)',
                            }}
                        >
                            {/* Row 1: Avatar + Name + ID */}
                            <div className="flex items-center gap-3 px-4 pt-3.5 pb-2.5">
                                <div
                                    className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center text-[13px] font-black text-white"
                                    style={{ background: color }}
                                >
                                    {initials}
                                </div>
                                <div className="min-w-0">
                                    <p className="font-bold text-[14px] leading-tight truncate" style={{ color: 'var(--v2-text-primary)' }}>
                                        {row.name || 'Unknown Member'}
                                    </p>
                                    <p className="text-[11px] font-mono" style={{ color: 'var(--v2-text-muted)', letterSpacing: '0.03em' }}>
                                        ID: {shortId}
                                    </p>
                                </div>
                            </div>

                            {/* Row 2: Role badge + email (like Gender badge + DOB pills in Patient History) */}
                            <div
                                className="flex items-center gap-2 px-4 py-2 flex-wrap"
                                style={{ borderTop: '1px solid var(--v2-border-subtle)' }}
                            >
                                {row.role ? (
                                    <span
                                        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border"
                                        style={{ background: `${roleColor}12`, color: roleColor!, borderColor: `${roleColor}30` }}
                                    >
                                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: roleColor! }} />
                                        {row.role}
                                    </span>
                                ) : (
                                    <span
                                        className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider italic"
                                        style={{ background: 'var(--v2-bg-surface)', color: 'var(--v2-text-muted)', border: '1.5px dashed var(--v2-border-subtle)' }}
                                    >
                                        No role
                                    </span>
                                )}
                                <span className="text-[var(--v2-border-card)] select-none">·</span>
                                <div className="flex items-center gap-1 min-w-0 overflow-hidden">
                                    <Mail className="w-3 h-3 flex-shrink-0" style={{ color: 'var(--v2-text-muted)' }} />
                                    <span className="text-[11px] truncate" style={{ color: 'var(--v2-text-primary)' }}>{row.email || '—'}</span>
                                </div>
                            </div>

                            {/* Row 3: Actions — RoleSelector (assign/change role) + Remove button */}
                            <div
                                className="flex items-center justify-between gap-2 px-4 py-2"
                                style={{ borderTop: '1px solid var(--v2-border-subtle)', background: 'var(--v2-bg-card-inner)' }}
                            >
                                {/* Role editor on the left */}
                                {roles.length > 0 && onAssignRole && onRemoveRole && (
                                    <RoleSelector
                                        email={row.email}
                                        currentRole={row.role}
                                        roles={roles}
                                        onAssign={onAssignRole}
                                        onRemove={onRemoveRole}
                                    />
                                )}
                                <div className="flex-1" />
                                {/* Remove on the right */}
                                {onDelete && (
                                    <button
                                        onClick={() => onDelete(row)}
                                        className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold rounded-lg transition-all duration-150 active:scale-95"
                                        style={{
                                            color: '#ef4444',
                                            background: 'rgba(239,68,68,0.07)',
                                            border: '1px solid rgba(239,68,68,0.15)',
                                        }}
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                        Remove
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const TeamLayout: React.FC<TeamLayoutProps> = ({
    data,
    loading = false,
    error,
    onDelete,
    scale = 1,
    roles = [],
    onAssignRole,
    onRemoveRole,
    onAddMember,
    onRetry,
    search = '',
}) => {
    const [sortDir, setSortDir] = useState<SortDir>(null);
    const handleSort = () => setSortDir(prev => prev === 'asc' ? 'desc' : prev === 'desc' ? null : 'asc');

    const filteredData = useMemo(() => {
        let result = data ?? [];
        if (search.trim()) {
            const q = search.toLowerCase();
            result = result.filter(m =>
                (m.name || '').toLowerCase().includes(q) ||
                (m.email || '').toLowerCase().includes(q) ||
                (m.role || '').toLowerCase().includes(q)
            );
        }
        if (sortDir) {
            result = [...result].sort((a, b) => {
                const na = (a.name || '').toLowerCase();
                const nb = (b.name || '').toLowerCase();
                return sortDir === 'asc' ? na.localeCompare(nb) : nb.localeCompare(na);
            });
        }
        return result;
    }, [data, search, sortDir]);

    if (loading) {
        return <div style={{ zoom: scale }}><TeamSkeleton /></div>;
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
                <div className="w-16 h-16 mb-4 rounded-full flex items-center justify-center" style={{ background: 'rgba(239,68,68,0.08)' }}>
                    <AlertCircle className="w-8 h-8" style={{ color: '#ef4444' }} />
                </div>
                <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--v2-text-heading)' }}>Failed to load members</h3>
                <p className="text-[14px] mb-4" style={{ color: 'var(--v2-text-muted)' }}>{error}</p>
                {onRetry && (
                    <V2Button variant="secondary" size="small" onClick={onRetry}>
                        Try Again
                    </V2Button>
                )}
            </div>
        );
    }

    if (!data || data.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
                {/* Matches Patient History empty state style exactly */}
                <div className="w-24 h-24 mb-6 rounded-full flex items-center justify-center bg-[var(--v2-bg-card-inner)]">
                    <div className="w-12 h-12 text-[var(--v2-text-muted)]">
                        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                                d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2m8-10a4 4 0 100-8 4 4 0 000 8zm11-1l-3 3m0 0l-3-3m3 3V7" />
                        </svg>
                    </div>
                </div>
                <h3 className="text-xl font-bold mb-2 text-[var(--v2-text-heading)]">No team members yet</h3>
                <p className="max-w-md mx-auto text-[15px] text-[var(--v2-text-muted)] mb-6">
                    No members found. Invite teammates to collaborate and assign roles.
                </p>
                {onAddMember && (
                    <V2Button variant="primary" size="small" onClick={onAddMember}>
                        Add Member
                    </V2Button>
                )}
            </div>
        );
    }

    return (
        <div style={{ zoom: scale }}>
            {filteredData.length === 0 && search.trim() ? (
                <NoResultsState query={search} />
            ) : (
                <MembersView
                    data={filteredData}
                    roles={roles}
                    onDelete={onDelete}
                    onAssignRole={onAssignRole}
                    onRemoveRole={onRemoveRole}
                    sortDir={sortDir}
                    onSort={handleSort}
                />
            )}
        </div>
    );
};

export default TeamLayout;
